// src/components/DocumentListModal.tsx
import React, { useState } from "react";
import { FileText, ChevronRight, ExternalLink, Search, SortAsc, SortDesc, XCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocumentLink, EnhancedDocumentLink, SortField, SortDirection } from "@/types";

interface DocumentListModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: EnhancedDocumentLink[];
}

const DocumentListModal: React.FC<DocumentListModalProps> = ({ 
  isOpen, 
  onClose, 
  documents 
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>('hierarchicalNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Switch direction if clicking on the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending sorting
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter documents based on search term
  const filteredDocuments = documents.filter(doc => {
    const searchLower = searchTerm.toLowerCase();
    
    // Search in all relevant fields
    return (
      doc.title.toLowerCase().includes(searchLower) ||
      (doc.process && doc.process.toLowerCase().includes(searchLower)) ||
      (doc.subProcess && doc.subProcess.toLowerCase().includes(searchLower)) ||
      (doc.moment && doc.moment.toLowerCase().includes(searchLower))
    );
  });

  // Sort documents
  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';

    // Special handling for hierarchicalNumber
    if (sortField === 'hierarchicalNumber' && a.hierarchicalNumber && b.hierarchicalNumber) {
      const aNumParts = a.hierarchicalNumber.split('.').map(Number);
      const bNumParts = b.hierarchicalNumber.split('.').map(Number);
      
      // Compare each part in the hierarchy
      for (let i = 0; i < Math.min(aNumParts.length, bNumParts.length); i++) {
        if (aNumParts[i] !== bNumParts[i]) {
          return sortDirection === 'asc' 
            ? aNumParts[i] - bNumParts[i]
            : bNumParts[i] - aNumParts[i];
        }
      }
      
      // If all parts are the same, use the length (shorter first)
      return sortDirection === 'asc'
        ? aNumParts.length - bNumParts.length
        : bNumParts.length - aNumParts.length;
    }

    // Normal string comparison
    const comparison = String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Group documents by process and subProcess
  const groupedDocuments: {[key: string]: EnhancedDocumentLink[]} = {};
  sortedDocuments.forEach(doc => {
    const groupKey = `${doc.process || ''} > ${doc.subProcess || ''}`;
    if (!groupedDocuments[groupKey]) {
      groupedDocuments[groupKey] = [];
    }
    groupedDocuments[groupKey].push(doc);
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-6xl bg-gray-900/95 border-gray-700 rounded-lg p-6"
      >
        <DialogDescription className="sr-only">
          Lista över dokument med sortering och filtreringsmöjligheter
        </DialogDescription>
        
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <FileText className="h-5 w-5 text-orange-300" />
            <span>Dokumentlista</span>
            <span className="ml-2 text-sm text-gray-400">({filteredDocuments.length} dokument)</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              type="text"
              placeholder="Sök dokument, process eller moment..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus-visible:ring-orange-300/50"
            />
            {searchTerm && (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                onClick={() => setSearchTerm("")}
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar rounded-lg bg-gray-800/50 border border-gray-700">
          {Object.keys(groupedDocuments).length > 0 ? (
            <Table>
              <TableHeader className="bg-gray-800/80 sticky top-0">
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:text-orange-300 transition-colors w-1/3"
                    onClick={() => handleSort('title')}
                  >
                    <div className="flex items-center gap-1">
                      Dokument
                      {sortField === 'title' && (
                        sortDirection === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:text-orange-300 transition-colors"
                    onClick={() => handleSort('process')}
                  >
                    <div className="flex items-center gap-1">
                      Process
                      {sortField === 'process' && (
                        sortDirection === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:text-orange-300 transition-colors"
                    onClick={() => handleSort('moment')}
                  >
                    <div className="flex items-center gap-1">
                      Moment
                      {sortField === 'moment' && (
                        sortDirection === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              
              <TableBody>
                <AnimatePresence>
                  {Object.entries(groupedDocuments).map(([groupKey, docs], groupIndex) => {
                    // Extract process name and subprocess from groupKey
                    const [process, subProcess] = groupKey.split(" > ");
                    const hasValidProcess = process && process !== "undefined";
                    
                    return (
                      <React.Fragment key={groupKey}>
                        {/* Show group header if we have a valid process */}
                        {hasValidProcess && (
                          <TableRow className="bg-gray-800/30 hover:bg-gray-800/50">
                            <TableCell colSpan={5} className="font-medium text-orange-300">
                              {process} {subProcess && <ChevronRight className="inline h-3 w-3 mx-1" />} {subProcess}
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {/* Show documents in the group */}
                        {docs.map((doc, index) => (
                          <motion.tr
                            key={`${doc.title}-${index}`}
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="border-t border-gray-800 hover:bg-gray-800/50 cursor-pointer"
                            onClick={() => doc.url && window.open(doc.url, "_blank")}
                          >
                            <TableCell className="font-mono text-xs text-gray-400">
                              {doc.hierarchicalNumber || ""}
                            </TableCell>
                            <TableCell className="font-medium text-blue-300">
                              {doc.title}
                            </TableCell>
                            <TableCell className="text-gray-300">
                              {doc.process || ""}
                            </TableCell>
                            <TableCell className="text-gray-300">
                              {doc.moment || ""}
                            </TableCell>
                            <TableCell>
                              {doc.url && (
                                <button
                                  type="button"
                                  className="h-8 w-8 p-0 text-gray-400 hover:text-blue-300"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(doc.url, "_blank")
                                  }}
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </button>
                              )}
                            </TableCell>
                          </motion.tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </AnimatePresence>
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-gray-400">
              {searchTerm ? (
                <>
                  <p>Inga dokument matchar sökningen "{searchTerm}"</p>
                  <Button 
                    variant="link" 
                    className="text-orange-300 mt-2"
                    onClick={() => setSearchTerm("")}
                  >
                    Rensa sökning
                  </Button>
                </>
              ) : (
                <p>Inga dokument tillgängliga</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentListModal;